import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { t } from '../lib/i18n';
import { useTablePrefs } from '../app/preferences-store';
import { EmptyState, ErrorState, Loading } from './Page';

/**
 * A table that knows about its own states — and, given a `tableKey`, about the person using it.
 *
 * Every list in the product is loading, then empty or full, and sometimes broken. Handing the table
 * those states means a screen cannot forget one — the commonest way a list ends up showing a blank
 * rectangle when the request failed.
 *
 * With a `tableKey` the table also offers a column chooser, a quick filter and pages, and remembers
 * the columns and the page size on the account (docs/PRODUCT.md): the arrangement follows the person
 * to the next machine, and a column a screen keeps hidden by default (`hidden: true`) is one click
 * away rather than absent.
 */

export interface Column<T> {
  key: string;
  header: string;
  /** Cells are rendered, not stringified: a status is a badge, a date is formatted. */
  render: (row: T) => ReactNode;
  /** Columns that can be dropped first when the screen is narrow. */
  secondary?: boolean;
  align?: 'start' | 'end';
  /** Off by default; the column chooser turns it on. */
  hidden?: boolean;
  /** What the quick filter matches against; defaults to the row's property of the same key. */
  text?: (row: T) => string;
  /**
   * The server-side field this column sorts by.
   *
   * Only meaningful together with the table's `sort` prop. Sorting is the server's job on a list
   * that is paged there (context.md section 134): re-ordering the fifty rows the browser happens
   * to hold would put the wrong fifty at the top.
   */
  sortField?: string;
}

export interface SortState {
  field: string;
  direction: 'asc' | 'desc';
}

const PAGE_SIZES = [10, 25, 50, 100];

function textOf<T>(column: Column<T>, row: T): string {
  if (column.text) return column.text(row);

  const value = (row as Record<string, unknown>)[column.key];

  return value === null || value === undefined || typeof value === 'object' ? '' : String(value);
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  isLoading,
  error,
  onRetry,
  onRowClick,
  empty,
  tableKey,
  pageSize: pageSizeProp,
  sort,
  more,
  quickFilter = true,
}: {
  rows: T[] | undefined;
  columns: Column<T>[];
  rowKey: (row: T) => string;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  onRowClick?: (row: T) => void;
  empty: { title: string; description?: string; action?: ReactNode };
  /** Names the table so its arrangement is remembered; without it the table is the plain kind. */
  tableKey?: string;
  pageSize?: number;
  /** Given this, a column with a `sortField` gets a header that asks the server to re-order. */
  sort?: { value: SortState; onChange: (next: SortState) => void };
  /**
   * More rows the server has and this table has not been given yet.
   *
   * Without it the pager walks a fixed slice — whatever the screen fetched once — and a list of
   * three hundred contacts is a list of the first fifty with a last page that looks like the end.
   * The count above the table says three hundred, which makes it worse: the screen contradicts
   * itself and the reader believes the number, not the rows.
   *
   * With it, stepping off the final local page asks for the next one instead of being refused.
   */
  more?: { hasMore: boolean; loading: boolean; onLoadMore: () => void };
  /**
   * Whether the table offers its own box for narrowing the rows in hand.
   *
   * Off for a screen that searches on the server. Two search boxes stacked on one screen is already
   * confusing, and with paging it is worse than confusing: the local one narrows only what has been
   * fetched and reports "3 of 50" while the server holds three hundred. A filter that quietly
   * excludes most of the data is a filter that gives wrong answers.
   */
  quickFilter?: boolean;
}): React.JSX.Element {
  const { prefs, save } = useTablePrefs(tableKey);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [choosing, setChoosing] = useState(false);
  /** Set when the reader asked for rows the table did not have; cleared when they arrive. */
  const wantMore = useRef(false);
  const chooser = useRef<HTMLDivElement>(null);

  const interactive = tableKey !== undefined;
  const pageSize = prefs.pageSize ?? pageSizeProp ?? 25;

  const defaultVisible = useMemo(
    () => columns.filter((c) => !c.hidden).map((c) => c.key),
    [columns],
  );
  const visibleKeys = useMemo(() => {
    const chosen = prefs.columns;
    if (!chosen) return defaultVisible;
    const known = new Set(columns.map((c) => c.key));
    const kept = chosen.filter((key) => known.has(key));

    return kept.length > 0 ? kept : defaultVisible;
  }, [prefs.columns, columns, defaultVisible]);
  const visible = columns.filter((column) => visibleKeys.includes(column.key));

  useEffect(() => {
    if (!choosing) return;
    const onClick = (event: MouseEvent): void => {
      if (!chooser.current?.contains(event.target as Node)) setChoosing(false);
    };
    document.addEventListener('mousedown', onClick);

    return () => document.removeEventListener('mousedown', onClick);
  }, [choosing]);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((row) =>
      columns.some((column) => textOf(column, row).toLowerCase().includes(needle)),
    );
  }, [rows, query, columns]);

  /*
   * A new search, or a different page size, starts at the beginning.
   *
   * Deliberately not keyed on `rows.length`: loading more rows grows it, and resetting then would
   * throw the reader back to page one every time they asked for the next page.
   */
  useEffect(() => {
    setPage(0);
  }, [query, pageSize]);

  /** Advances onto the rows that were just fetched, once they are actually here. */
  useEffect(() => {
    if (!wantMore.current || more?.loading) return;

    wantMore.current = false;

    const grown = Math.max(1, Math.ceil((rows?.length ?? 0) / pageSize));

    setPage((current) => Math.min(current + 1, grown - 1));
  }, [rows?.length, more?.loading, pageSize]);

  if (error) return <ErrorState error={error} {...(onRetry ? { onRetry } : {})} />;
  if (isLoading && !rows) return <Loading />;
  if (!rows || rows.length === 0) return <EmptyState {...empty} />;

  const total = filtered?.length ?? 0;
  const pages = interactive ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const current = Math.min(page, pages - 1);
  const pageRows = interactive
    ? (filtered ?? []).slice(current * pageSize, (current + 1) * pageSize)
    : (filtered ?? []);

  const toggleColumn = (key: string): void => {
    const next = visibleKeys.includes(key)
      ? visibleKeys.filter((k) => k !== key)
      : columns.filter((c) => c.key === key || visibleKeys.includes(c.key)).map((c) => c.key);
    if (next.length === 0) return;
    save({ ...prefs, columns: next });
  };

  return (
    <div className="table-wrap">
      {interactive && (
        <div className="table-tools">
          {quickFilter && (
            <>
              <input
                type="search"
                className="table-tools__filter"
                value={query}
                placeholder={t('table.filter')}
                aria-label={t('table.filter')}
                onChange={(event) => setQuery(event.target.value)}
              />

              <span className="muted small">
                {query
                  ? t('table.matching')
                      .replace('{n}', String(total))
                      .replace('{total}', String(rows.length))
                  : t('table.count').replace('{n}', String(rows.length))}
              </span>
            </>
          )}

          <div className="table-tools__chooser" ref={chooser}>
            <button
              type="button"
              className="button button--quiet button--compact"
              aria-expanded={choosing}
              onClick={() => setChoosing((c) => !c)}
            >
              {t('table.columns')}
            </button>
            {choosing && (
              <div className="table-tools__menu" role="menu">
                {/*
                 * Only columns that have a name.
                 *
                 * A screen's actions column carries buttons and no header, by convention across
                 * the app. Offering it as a nameless checkbox would be offering somebody the
                 * chance to hide the only controls in the row, without telling them what it is.
                 */}
                {columns
                  .filter((column) => column.header !== '')
                  .map((column) => (
                    <label key={column.key} className="table-tools__option">
                      <input
                        type="checkbox"
                        checked={visibleKeys.includes(column.key)}
                        onChange={() => toggleColumn(column.key)}
                      />
                      {column.header}
                    </label>
                  ))}
                {prefs.columns && (
                  <button
                    type="button"
                    className="link small"
                    onClick={() => save({ ...prefs, columns: undefined })}
                  >
                    {t('table.resetColumns')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <table className="table">
        <thead>
          <tr>
            {visible.map((column) => (
              <th
                key={column.key}
                className={`${column.secondary ? 'table__secondary' : ''} ${
                  column.align === 'end' ? 'table__end' : ''
                }`.trim()}
                scope="col"
                aria-sort={
                  sort && column.sortField && sort.value.field === column.sortField
                    ? sort.value.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
              >
                {sort && column.sortField ? (
                  <button
                    type="button"
                    className="table__sort"
                    onClick={() =>
                      sort.onChange({
                        field: column.sortField!,
                        // Clicking the column already sorted reverses it; a new column starts
                        // descending, which for a date and a number is the interesting end.
                        direction:
                          sort.value.field === column.sortField && sort.value.direction === 'desc'
                            ? 'asc'
                            : 'desc',
                      })
                    }
                  >
                    {column.header}
                    {sort.value.field === column.sortField && (
                      <span aria-hidden="true">
                        {sort.value.direction === 'desc' ? ' ↓' : ' ↑'}
                      </span>
                    )}
                  </button>
                ) : (
                  column.header
                )}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {pageRows.length === 0 ? (
            <tr>
              <td colSpan={visible.length} className="muted">
                {t('table.noMatch')}
              </td>
            </tr>
          ) : (
            pageRows.map((row) => (
              <tr
                key={rowKey(row)}
                {...(onRowClick
                  ? {
                      onClick: () => onRowClick(row),
                      className: 'table__row--clickable',
                      tabIndex: 0,
                      // A row that acts like a link has to work from the keyboard too.
                      onKeyDown: (event: React.KeyboardEvent) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowClick(row);
                        }
                      },
                    }
                  : {})}
              >
                {visible.map((column) => (
                  <td
                    key={column.key}
                    className={`${column.secondary ? 'table__secondary' : ''} ${
                      column.align === 'end' ? 'table__end' : ''
                    }`.trim()}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/*
        Shown whenever there is somewhere to go.
        
        `more.hasMore` belongs in this test: a first page that happens to fill exactly one local
        page would otherwise hide the pager entirely, and with it the only way to reach the rest.
      */}
      {interactive && (pages > 1 || total > PAGE_SIZES[0]! || more?.hasMore === true) && (
        <div className="table-pager">
          <label className="cell-inline small">
            {t('table.perPage')}
            <select
              className="select select--compact"
              value={pageSize}
              onChange={(event) => save({ ...prefs, pageSize: Number(event.target.value) })}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <span className="cell-inline">
            <button
              type="button"
              className="button button--quiet button--compact"
              disabled={current === 0}
              onClick={() => setPage(current - 1)}
            >
              ‹
            </button>
            <span className="small">
              {t('table.page')
                .replace('{n}', String(current + 1))
                // A "+" while the server still has more: this is the last page *so far*, and
                // printing "3 de 3" over a list of three hundred is the lie being fixed.
                .replace('{pages}', more?.hasMore ? `${pages}+` : String(pages))}
            </span>
            <button
              type="button"
              className="button button--quiet button--compact"
              // The last local page is the end only when the server agrees it is.
              disabled={(current >= pages - 1 && !more?.hasMore) || more?.loading === true}
              onClick={() => {
                if (current < pages - 1) {
                  setPage(current + 1);
                  return;
                }

                /*
                 * Fetching, then advancing.
                 *
                 * The page is not moved here: the new rows arrive asynchronously and `pages` is
                 * derived from them, so moving first would land on a page that does not exist yet
                 * and get clamped straight back. The effect below steps forward once they land.
                 */
                wantMore.current = true;
                more?.onLoadMore();
              }}
            >
              {more?.loading ? '…' : '›'}
            </button>
          </span>
        </div>
      )}
    </div>
  );
}
